import { Component, Suspense, lazy, useState, type ReactNode } from "react";
import { useAuthStore } from "../../stores/authStore";

/**
 * Loads the ConfigIQ Module Federation remote.
 *
 * The heavy lifting lives in vite.config.ts's federation() host config:
 * import("configiq/App") below is rewritten by the plugin into a runtime
 * fetch of /configiq-remote/assets/remoteEntry.js (routed by Caddy/
 * Traefik to configiq-web), with react/react-dom/react-router shared as
 * singletons so the remote renders inside the HOST's React tree and
 * router context. Platform never rebuilds when ConfigIQ changes — the
 * remote URL is fetched at runtime, not bundled.
 *
 * The previous implementation here hand-rolled the webpack federation
 * loading pattern (script tag → window["configiq"] global →
 * __webpack_share_scopes__) against a Vite-built ESM remote, which can
 * never work: the ESM remoteEntry registers no window global, and
 * __webpack_share_scopes__ is a webpack-only global whose bare mention
 * throws a ReferenceError in a Vite host (optional chaining does not
 * guard undeclared identifiers). That is why ConfigIQ never rendered
 * once in deployment.
 *
 * Note for local dev: `vite dev` on Platform alone shows the error
 * state on /configiq unless the remote is actually being served at
 * /configiq-remote/ — the remote must be built (`pnpm build` in
 * configiq/apps/web) and reachable; it is not part of Platform's dev
 * server.
 */
const ConfigIQApp = lazy(() => import("configiq/App"));

function ConfigIQError() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
        <span className="text-2xl">⚠️</span>
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-900">ConfigIQ is unavailable</p>
        <p className="text-xs text-gray-500 mt-1">The service may be starting up. Try refreshing.</p>
      </div>
      <button onClick={() => window.location.reload()}
        className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
        Refresh
      </button>
    </div>
  );
}

function ConfigIQSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-8 bg-gray-200 rounded-lg w-48" />
      <div className="h-4 bg-gray-100 rounded w-72" />
      <div className="grid grid-cols-2 gap-3 mt-6">
        {[1,2,3,4].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl" />)}
      </div>
    </div>
  );
}

class ErrorBoundaryWrapper extends Component<{ children: ReactNode; onError: () => void }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: unknown) {
    // Surface the real failure in the console — the previous loader
    // swallowed it entirely, which made "never loaded once" much harder
    // to diagnose than it should have been.
    console.error("[ConfigIQ] remote failed to load:", error);
    this.props.onError();
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

export function ConfigIQLoader() {
  const [error, setError] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.platformRole === "platform_admin";

  // useAuthStore.getState() (Zustand's static accessor, not the hook) —
  // reads whatever accessToken is current AT CALL TIME, not the value
  // captured when this component last rendered. ConfigIQ's api.ts calls
  // this fresh on every single request, so it survives Platform's own
  // token-rotation/refresh cycle without ConfigIQLoader needing to
  // re-render or pass a new prop reference each time the token changes.
  // A useCallback-memoized closure over the hook's `accessToken` value
  // would NOT have this property — it would capture whatever value was
  // current at the render that created the closure, going stale exactly
  // like the bug this whole fix addresses.
  const getAccessToken = () => useAuthStore.getState().accessToken;

  if (error) return <ConfigIQError />;

  return (
    <Suspense fallback={<ConfigIQSkeleton />}>
      <ErrorBoundaryWrapper onError={() => setError(true)}>
        <ConfigIQApp
          apiBaseUrl={import.meta.env.VITE_API_URL ?? ""}
          basePath="/configiq"
          isPlatformAdmin={isPlatformAdmin}
          getAccessToken={getAccessToken}
        />
      </ErrorBoundaryWrapper>
    </Suspense>
  );
}
